import type { SeoParams } from '@/lib/useSeo';
import { PROD_ORIGIN, canonicalPath } from '@workspace/url-source';

/**
 * Centralny config SEO (title/description i inne statyczne parametry
 * useSeo()) dla stron, których treść SEO jest stałym literałem — nie
 * pochodzi z Shopify ani z innego configu strony. Strony, które już ciągną
 * title/description z danych (product.tsx, collection.tsx,
 * CategoryCollectionPage.tsx, FuneralCollectionPage.tsx, blog-artykul.tsx,
 * dzielnica.tsx) nie są tu — mają już jedno źródło prawdy gdzie indziej.
 */
export const PAGE_SEO = {
  home: {
    title: 'Kwiaciarnia Lublin — Kwiaty i bukiety z dostawą',
    description: 'Bukietowa to kwiaciarnia w Lublinie. Zamawiaj bukiety, kwiaty cięte i kompozycje online. Dostawa tego samego dnia — Lublin i okolice.',
  },
  blog: {
    title: 'Blog florystyczny — Porady i inspiracje',
    description: 'Florystyczne porady, inspiracje i trendy od kwiaciarni Bukietowa w Lublinie. Czytaj o pielęgnacji kwiatów i aranżacjach.',
  },
  zamowienieSpecjalne: {
    title: 'Zamówienie specjalne — Bukiety i dekoracje na miarę',
    description: 'Zamów wyjątkową kompozycję kwiatową na specjalną okazję. Indywidualne zamówienia florystyczne — Bukietowa Lublin.',
  },
  oNas: {
    title: 'O nas — Historia i zespół Bukietowej',
    description: 'Poznaj historię i zespół kwiaciarni Bukietowa w Lublinie. Florystyczna pasja, świeże kwiaty i bukiety tworzone z miłości do roślin.',
  },
  warsztatyFlorystyczne: {
    title: 'Warsztaty florystyczne Lublin',
    description: 'Warsztaty florystyczne w Lublinie — naucz się tworzyć bukiety i dekoracje kwiatowe. Bukietowa zaprasza na kreatywne zajęcia.',
  },
  faq: {
    title: 'FAQ — Najczęstsze pytania',
    description: 'Odpowiedzi na najczęstsze pytania o zamówienia, dostawę i kwiaty w kwiaciarni Bukietowa w Lublinie.',
  },
  kreatory: {
    title: 'Kreatory bukietów online',
    description: 'Zaprojektuj własny bukiet online. Wybierz kwiaty, kolor i ilość. Dostawa w Lublinie tego samego dnia — Bukietowa.',
    // Ta sama strona jest zarejestrowana pod dwoma trasami (/kreatory i
    // /kreatory-bukietow — patrz App.tsx). Bez jawnego canonicala obie
    // samo-kanonikalizowałyby się osobno (duplicate content). Kanoniczny
    // URL to /kreatory-bukietow — zgodny z nawigacją (navigationData.ts) i sitemapą.
    canonical: PROD_ORIGIN + canonicalPath('/kreatory-bukietow'),
  },
  pogrzeb: {
    title: 'Kwiaty pogrzebowe Lublin — Wieńce i kondolencje',
    description: 'Wieńce, wiązanki i kompozycje pogrzebowe z dostawą w Lublinie. Kwiaty kondolencyjne — Bukietowa Lublin.',
    // Zarejestrowana pod /pogrzeb i /kompozycje-pogrzebowe — kanoniczny URL
    // zgodny z HANDLE_TO_URL['pogrzeb'] w @workspace/url-source.
    canonical: PROD_ORIGIN + canonicalPath('/kompozycje-pogrzebowe'),
  },
  prywatnosc: {
    title: 'Polityka prywatności',
    description: 'Zasady przetwarzania danych osobowych w Kwiaciarni Bukietowa — jakie dane zbieramy, w jakim celu i jakie masz prawa.',
    robots: 'noindex, follow',
  },
  dziekujemy: {
    title: 'Dziękujemy za zamówienie',
    robots: 'noindex,follow',
  },
  kwiaty: {
    title: 'Kwiaty cięte i doniczkowe — Lublin',
    description: 'Szeroki wybór kwiatów ciętych i doniczkowych w kwiaciarni Bukietowa w Lublinie. Dostarczamy kwiaty tego samego dnia.',
  },
  okazje: {
    title: 'Okazje — kwiaty na każdą chwilę | Bukietowa',
    description: 'Znajdź kwiaty dopasowane do każdej okazji: urodziny, rocznice, imieniny, święta i wiele więcej. Dostawa w Lublinie.',
    noSuffix: true,
  },
  sklep: {
    title: 'Sklep — Kwiaty i bukiety online',
    description: 'Przeglądaj pełną ofertę kwiatów, bukietów i kompozycji kwiatowych Bukietowej. Zamów z dostawą w Lublinie i okolicach.',
  },
  kontakt: {
    title: 'Kontakt — Bukietowa Kwiaciarnia Lublin',
    description: 'Skontaktuj się z kwiaciarnią Bukietowa w Lublinie. Telefon, e-mail, adres i godziny otwarcia.',
    noSuffix: true,
  },
  dlaFirm: {
    title: 'Kwiaty dla firm Lublin — Florystyka biznesowa',
    description: 'Stała obsługa florystyczna firm w Lublinie. Kwiaty do biur, restauracji i na eventy. Bukietowa — profesjonalna florystyka biznesowa.',
  },
  bukiety: {
    title: 'Bukiety kwiatów — Lublin',
    description: 'Odkryj nasze bukiety kwiatów przygotowywane przez florystów Bukietowej w Lublinie. Świeże kompozycje na urodziny, rocznice i wyjątkowe okazje.',
  },
  slub: {
    title: 'Kwiaty ślubne Lublin — Bukiety i dekoracje weselne',
    description: 'Bukietowa oferuje kwiaty ślubne, bukiety Panny Młodej i dekoracje florystyczne na wesele. Lublin i okolice.',
  },
  flowerBoxy: {
    title: 'Flower Boxy — Lublin | Bukietowa',
    description: 'Eleganckie flower boxy z żywych kwiatów — gotowe kompozycje w pudełku, idealne na prezent. Zamów z dostawą w Lublinie.',
    noSuffix: true,
    // Zarejestrowana pod /flower-boxy i /kolekcje/flowerbox — kanoniczny URL
    // zgodny z HANDLE_TO_URL['flowerbox'] w @workspace/url-source.
    canonical: PROD_ORIGIN + canonicalPath('/flower-boxy'),
  },
  politykaZwrotow: {
    title: 'Polityka zwrotów',
    description: 'Zasady zwrotów i reklamacji w Kwiaciarni Bukietowa — jak i w jakim terminie możesz zwrócić lub zareklamować zamówienie.',
    robots: 'noindex, follow',
  },
  ulubione: {
    title: 'Ulubione produkty',
    description: 'Twoje ulubione bukiety i kompozycje kwiatowe w Bukietowej.',
    robots: 'noindex,follow',
  },
  regulamin: {
    title: 'Regulamin',
    description: 'Regulamin sklepu internetowego Kwiaciarni Bukietowa — zasady składania zamówień, płatności, dostawy i reklamacji.',
    robots: 'noindex, follow',
  },
  dostawa: {
    title: 'Dostawa i płatność — Bukietowa Lublin',
    description: 'Dostawa kwiatów w Lublinie 7 dni w tygodniu. Zamówienia na dziś pn.–sob. do 18:00, w niedzielę do 15:00. Płatność kartą, BLIK-iem lub przelewem online przez Stripe.',
    noSuffix: true,
  },
  bestsellery: {
    title: 'Bestsellery — Najpopularniejsze bukiety',
    description: 'Nasze bestsellery — najchętniej wybierane bukiety i kompozycje kwiatowe w Bukietowej Lublin. Sprawdzone i uwielbiane przez klientów.',
  },
} satisfies Record<string, SeoParams>;
